import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ShieldOff, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SessionSecurityCard
 * Lets the current user revoke every session tied to their account
 * (this device included). On success we clear local state and force
 * a redirect to /login so nothing keeps a stale token in memory.
 */
export default function SessionSecurityCard({ onRevoked } = {}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const revoke = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/revoke-all-sessions`, {}, {
        headers: authHeaders(),
        withCredentials: true,
      });
      toast.success('All sessions signed out. Please log in again.');
      // Nuke locally cached auth material.
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (typeof onRevoked === 'function') onRevoked();
      setTimeout(() => navigate('/login'), 400);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to revoke sessions';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="session-security-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldOff className="w-5 h-5 text-amber-500" />
          Session security
        </CardTitle>
        <CardDescription>
          Sign out of every device that has ever logged in to this account. Useful if you
          suspect someone else has your password or if you left yourself signed in on a shared
          computer.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="destructive"
              className="bg-rose-600 hover:bg-rose-500"
              disabled={loading}
              data-testid="revoke-all-sessions-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldOff className="w-4 h-4 mr-2" />}
              Sign out everywhere
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-slate-900 border-slate-700">
            <AlertDialogHeader>
              <AlertDialogTitle>Sign out of every device?</AlertDialogTitle>
              <AlertDialogDescription>
                This immediately invalidates every browser and device signed in to your account —
                including this one. You&apos;ll need to log back in.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-slate-800 border-slate-700" data-testid="revoke-cancel">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={revoke}
                className="bg-rose-600 hover:bg-rose-500"
                data-testid="revoke-confirm"
              >
                Yes, sign me out everywhere
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
